import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import Parser from 'html-react-parser';
import omit from 'lodash/omit';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Link from '@mui/material/Link';
import classNames from 'classnames';
import { I18n } from 'i18n-js';
import CheckboxInput from '../common/CheckboxInput';
import ActionButton from '../common/ActionButton';
import PushSnackbarMessage from '../../helpers/PushSnackbarMessage';
import translations from '../../locales.json';

const i18n = new I18n(translations);

const styles = theme => ({
  form: {
    flexGrow: 1,
    width: '100%', // Fix IE 11 issue.
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
  },
  centerContent: {
    display: 'flex',
    justifyContent: 'center',
    justifyItems: 'center',
  },
  itemTitle: { marginBottom: '25px' },
  inputLabel: {
    [theme.breakpoints.down('xl')]: {
      fontSize: '0.875em',
    },
  },
  itemGrid: {
    [theme.breakpoints.up('lg')]: {
      padding: '5px !important',
    },
    [theme.breakpoints.down('xl')]: {
      padding: '2px !important',
    },
  },
  leftGrid: {
    paddingRight: '5px !important',
    '@media (min-width: 600px) and (max-width: 1024px) and (min-height: 1024px)': {
      padding: '2px !important',
      maxWidth: '100%',
      flexBasis: '100%',
    },
  },
  rightGrid: {
    paddingLeft: '5px !important',
    '@media (min-width: 600px) and (max-width: 1024px) and (min-height: 1024px)': {
      padding: '2px !important',
      maxWidth: '100%',
      flexBasis: '100%',
    },
  },
});

class RecruiterRegistrationForm extends Component {
  state = {
    showPassword: false,
    formAttrs: {},
    childNodes: '',
  }

  componentDidMount = () => {
    const originalForm = document.getElementById('recruiter-sign-up-form');
    if (!originalForm) { return; }
    const formAttrs = ['action', 'acceptCharset', 'method'].reduce((obj, val) => {
      const transformObj = obj;
      transformObj[val] = originalForm[val];
      return transformObj;
    }, {});
    const childNodes = originalForm.innerHTML;
    this.setState({
      formAttrs,
      childNodes,
    });
  };

  handleClickShowPassword = () => {
    this.setState(state => ({ showPassword: !state.showPassword }));
  };

  render() {
    const {
      values: {
        firstName, lastName, email, password, passwordConfirmation, validTermService,
      },
      errors,
      touched,
      handleChange,
      isValid,
      setFieldTouched,
      classes,
      enqueueSnackbar,
    } = this.props;

    const hiddenFields = [
      { name: 'recruiter[fname]', value: firstName },
      { name: 'recruiter[lname]', value: lastName },
      { name: 'recruiter[email]', value: email },
      { name: 'recruiter[password]', value: password },
      { name: 'recruiter[password_confirmation]', value: passwordConfirmation },
    ];

    const change = (e, name) => {
      e.persist();
      handleChange(e);
      setFieldTouched(name, true, false);
    };

    const {
      showPassword, formAttrs, childNodes,
    } = this.state;

    const hasError = field => (
      (touched[field] && Boolean(errors[field]))
        || (Boolean(errors[field]) && Boolean(!errors.passwordConfirmation))
    );

    const errorMessage = field => (
      (touched[field] || Boolean(!errors.passwordConfirmation)) ? errors[field] : ''
    );

    const showErrorOnSnackbar = () => {
      const currentErrors = Object.values(omit(errors, ['validTermService']));
      currentErrors.map(error => PushSnackbarMessage(enqueueSnackbar, error, 'warning'));
    };

    return (
      <form {...formAttrs} className={classes.form}>
        {hiddenFields.map(field => (
          <input
            type="hidden"
            key={field.name}
            name={field.name}
            value={field.value}
          />
        ))}
        <Grid
          container
          direction="row"
          justifyContent="space-evenly"
          alignItems="center"
          spacing={1}
        >
          <Grid
            item
            xs={12}
            sm={6}
            className={
              classNames({
                [classes.leftGrid]: true, [classes.itemGrid]: true,
              })
            }
          >
            <FormControl margin="dense" required fullWidth>
              <InputLabel htmlFor="firstName" className={classes.inputLabel}>
                {i18n.t('js.sign_up.recruiter.form.name.first')}
              </InputLabel>
              <Input
                id="firstName"
                value={firstName}
                error={hasError('firstName')}
                onChange={e => change(e, 'firstName')}
              />
              <FormHelperText>{errorMessage('firstName')}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={
              classNames({
                [classes.rightGrid]: true, [classes.itemGrid]: true,
              })
            }
          >
            <FormControl margin="dense" required fullWidth>
              <InputLabel htmlFor="lastName" className={classes.inputLabel}>
                {i18n.t('js.sign_up.recruiter.form.name.last')}
              </InputLabel>
              <Input
                id="lastName"
                value={lastName}
                error={hasError('lastName')}
                onChange={e => change(e, 'lastName')}
              />
              <FormHelperText>{errorMessage('lastName')}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} className={classes.itemGrid}>
            <FormControl margin="dense" required fullWidth>
              <InputLabel htmlFor="email" className={classes.inputLabel}>
                {i18n.t('js.sign_up.recruiter.form.email.business')}
              </InputLabel>
              <Input
                id="email"
                value={email}
                error={hasError('email')}
                onChange={e => change(e, 'email')}
              />
              <FormHelperText>{errorMessage('email')}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} className={classes.itemGrid}>
            <FormControl margin="dense" required fullWidth>
              <InputLabel htmlFor="password" className={classes.inputLabel}>
                {i18n.t('js.sign_up.recruiter.form.password.password')}
              </InputLabel>
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                error={hasError('password')}
                onChange={e => change(e, 'password')}
                endAdornment={(
                  <InputAdornment position="end">
                    <IconButton
                      tabIndex={-1}
                      aria-label={i18n.t('js.sign_up.recruiter.form.button.label_password_visibility')}
                      onClick={this.handleClickShowPassword}
                      size="large"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                  )}
                placeholder={i18n.t('js.sign_up.recruiter.form.password.condition')}
              />
              <FormHelperText>{errorMessage('password')}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} className={classes.itemGrid}>
            <FormControl margin="dense" required fullWidth>
              <InputLabel htmlFor="passwordConfirmation" className={classes.inputLabel}>
                {i18n.t('js.sign_up.recruiter.form.password.confirmation')}
              </InputLabel>
              <Input
                id="passwordConfirmation"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={passwordConfirmation}
                error={touched.passwordConfirmation && Boolean(errors.passwordConfirmation)}
                onChange={e => change(e, 'passwordConfirmation')}
                onBlur={showErrorOnSnackbar}
                endAdornment={(
                  <InputAdornment position="end">
                    <IconButton
                      tabIndex={-1}
                      aria-label="Toggle password visibility"
                      onClick={this.handleClickShowPassword}
                      size="large"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                  )}
              />
              <FormHelperText>{touched.passwordConfirmation ? errors.passwordConfirmation : ''}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} className={classes.itemGrid}>
            <CheckboxInput
              name="validTermService"
              checked={validTermService}
              onChange={e => change(e, 'validTermService')}
              value={i18n.t('js.sign_up.recruiter.form.term_service.agree')}
              label={(
                <div>
                  <span>{i18n.t('js.sign_up.recruiter.form.term_service.agree')}</span>
                  &nbsp;
                  <Link href="https://secure.rakuna.co/legal/Rakuna_Members_TOS.pdf" target="_blank">
                    {i18n.t('js.sign_up.recruiter.form.term_service.terms')}
                  </Link>
                </div>
              )}
            />
          </Grid>
          <Grid item xs={12} className={classes.centerContent}>
            {Parser(childNodes)}
          </Grid>
          <Grid item sm={3} />
          <Grid item md={6} sm={12} xs={12}>
            <ActionButton isValid={isValid} type="submit" text={i18n.t('js.sign_up.recruiter.form.set_up_my_team_account')} />
          </Grid>
          <Grid item sm={3} />
        </Grid>
      </form>
    );
  }
}

RecruiterRegistrationForm.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  values: PropTypes.instanceOf(Object).isRequired,
  errors: PropTypes.instanceOf(Object).isRequired,
  touched: PropTypes.instanceOf(Object).isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
  isValid: PropTypes.bool.isRequired,
};

export default withStyles(styles)(RecruiterRegistrationForm);
